import { CircularProgress, Tooltip } from '@mui/material';
import { Controller } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PricingGuide } from './pricingGuide/PricingGuide';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { OfferDetails, usePricingModel } from './pricing.model';
import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import { Input } from '@design-system/components/input/input';
import { InputNumber } from '@design-system/components/input/inputNumber';
import {
  faCircleInfo,
  faDollar,
  faInfoCircle,
  faPercent,
} from '@fortawesome/pro-regular-svg-icons';
import { InputPrice } from '@design-system/components/input/inputPrice';
import { Collection } from '../collection.domain';
import { DiscountsCard } from './components/DiscountsCard';
import { Link } from 'react-router';
import { PATHS } from '@client/routes';
import { isEmpty } from 'lodash';
import { Chip } from '@design-system/components/chip/chip';
import { sprinkles } from '@design-system/sprinkles.css';

type ProgramPricingProps = {
  creatorId: number;
  collection: Collection;
  onPriceSubmitted: () => void;
  readonly: boolean;
};

export const ProgramPricing = (p: ProgramPricingProps) => {
  const model = usePricingModel(p);

  return (
    <AsyncRenderer
      data={model}
      loadRender={
        <div className="flex justify-center items-center">
          <CircularProgress />
        </div>
      }
      renderer={(data) => (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col lg:flex-row lg:flex-wrap grow gap-6">
            <div className="flex flex-col p-6 bg-white rounded-[20px] grow gap-4">
              <div className="flex flex-col">
                <div className="flex justify-between">
                  <div className="flex flex-col md:flex-row gap-2.5 md:items-end grow">
                    <Controller
                      name={'price'}
                      control={data.control}
                      render={({ field: { onChange, value }, fieldState }) => (
                        <InputPrice
                          label="Price"
                          required
                          disabled={data.submitting}
                          value={value}
                          onChange={onChange}
                          error={fieldState.error?.message}
                          startIcon={{ icon: faDollar }}
                          wrapperClassName="lg:w-[150px]"
                        />
                      )}
                    />
                    {!data.showFee ? null : (
                      <Controller
                        name={'playbookFee'}
                        control={data.control}
                        render={({
                          field: { onChange, value },
                          fieldState,
                        }) => (
                          <InputNumber
                            label="Playbook fee"
                            required
                            disabled={data.submitting || data.readonly}
                            value={value}
                            onChange={onChange}
                            error={fieldState.error?.message}
                            endIcon={{ icon: faPercent }}
                            wrapperClassName="lg:w-[150px]"
                          />
                        )}
                      />
                    )}
                    {!data.showFee ? null : (
                      <Input
                        label=""
                        disabled={true}
                        value={data?.playbookFeePrice}
                        startIcon={{ icon: faDollar }}
                        wrapperClassName="lg:w-[150px]"
                      />
                    )}
                    <Button
                      color="neutral"
                      className="h-[44px] min-w-[65px] m-0.25"
                      disabled={data.disabledSavePrice || data.submitting}
                      onClick={() => data.dispatch('savePrice')}
                    >
                      {data.submitting ? (
                        <CircularProgress size={20} />
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </div>
                  <Tooltip
                    placement="right"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'black',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: 500,
                          fontFamily: 'inherit',
                          padding: '4px 8px',
                        },
                      },
                    }}
                    title={
                      <div className="flex flex-col p-4">
                        <Typography>
                          Frequent and unreasonable price changes can frustrate
                          your customers. To build trust and maintain a positive
                          experience:
                        </Typography>
                        <ul className="list-disc p-2">
                          <li>
                            <Typography>
                              Avoid changing prices and discounts too often.
                            </Typography>
                          </li>
                          <li>
                            <Typography>
                              Make any necessary adjustments reasonable and
                              fair.
                            </Typography>
                          </li>
                        </ul>
                        <Typography>
                          Remember, consistency is key to keeping your customers
                          happy and engaged.
                        </Typography>
                      </div>
                    }
                  >
                    <div className="w-[20px]">
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </div>
                  </Tooltip>
                </div>
                <PricingGuide />
              </div>
            </div>
            {data.showDiscount ? <DiscountsCard {...data} /> : null}
          </div>
          <div className="flex">
            <FontAwesomeIcon
              icon={faCircleInfo}
              className="text-[#646464] pr-2"
            />
            <Typography className="text-[#646464]">
              To sell this product as part of the offer go to
            </Typography>
            <Link to={PATHS.BUNDLES(p.creatorId)}>
              <Typography className="text-[#646464] underline px-1">
                Offers
              </Typography>
            </Link>

            <Typography className="text-[#646464]">
              and create new one or edit existing.
            </Typography>
          </div>
          {!isEmpty(data.offers) && (
            <div className="flex flex-col p-6 bg-white rounded-[20px] grow gap-4">
              <div className="flex flex-col gap-2">
                <Typography variant="body" size="large">
                  Offers
                </Typography>
                <Typography
                  variant="body"
                  size="medium"
                  fontWeight="normal"
                  color="N-80"
                >
                  This program belongs to:
                </Typography>
              </div>
              <div className="flex flex-wrap gap-4">
                {data.offers.map(({ id, title, bgColor, opacity }) => (
                  <Chip
                    key={id}
                    readonly={true}
                    label={title}
                    className={sprinkles({
                      bg: { hover: bgColor, lightMode: bgColor },
                      opacity,
                    })}
                  />
                ))}
              </div>
            </div>
          )}
          {data.canRemovePricing ? (
            <div>
              <Button
                onClick={() => data.dispatch('removePricing')}
                disabled={data.submitting}
                className="bg-red-600"
              >
                Remove pricing
              </Button>
            </div>
          ) : null}
        </div>
      )}
    />
  );
};
